import { motion } from 'framer-motion';
import {
	BadgePercent,
	BanknoteArrowDown,
	Hash,
	ScanBarcode,
} from 'lucide-react';

import { useTheme } from '../../context/ThemeContext';
import { ScrollingText } from './ScrollingText';
import { Star } from './star';
import type { ProductProps } from './types';

type CardSaleProps = ProductProps;

function CardSale1(props: CardSaleProps) {
	const { settings } = useTheme();

	return (
		<div className="relative grid h-full w-full grid-cols-2">
			<div className="relative text-secondary">
				<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
					<motion.div
						animate={{ rotate: 360, scale: [1.2, 1.3] }}
						transition={{ duration: 20, repeat: Infinity }}
					>
						<Star />
					</motion.div>
				</div>
				<div className="absolute left-1/2 top-1/2 flex h-[70%] max-h-[80%] w-full -translate-x-1/2 -translate-y-1/2 justify-center">
					<motion.div
						initial={{ opacity: 0, y: -100 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 1.5 }}
						className="h-full w-[auto]"
					>
						<img
							src={props.imageUrl}
							alt={props.name}
							className="h-full w-auto rounded-[20px] object-cover"
						/>
					</motion.div>
				</div>
			</div>
			<motion.div className="xl:-mt-14 xl:pl-24 relative z-10 -mt-20 flex flex-col items-start justify-center pl-0 pr-6">
				<motion.h1
					initial={{ opacity: 0, transform: 'translateX(20%)' }}
					animate={{ opacity: 1, transform: 'translateX(0)' }}
					transition={{ duration: 1 }}
					className="mb-7 line-clamp-3 text-[clamp(2rem,4vw,3rem)] font-extrabold uppercase leading-[100%] text-text"
				>
					{props.name}
				</motion.h1>
				<div className="xl:mt-5 relative mt-2 pl-7">
					<motion.div
						initial={{ opacity: 0, transform: 'translateY(-20%)' }}
						animate={{ opacity: 1, transform: 'translateY(0)' }}
						transition={{ duration: 1, delay: 0.5 }}
						className="flex items-center gap-6 pl-5"
					>
						{props.fullPrice && (
							<p className="text-[clamp(1.25rem,2.5vw,1.875rem)] text-text">
								De{' '}
								<strong className="relative">
									R${' '}
									<span className="relative">
										<span className="font-bold">
											{props.fullPrice.toLocaleString('pt-BR', {
												minimumFractionDigits: 2,
												currency: 'BRL',
											})}
										</span>
										<span className="pointer-events-none absolute inset-0 before:absolute before:inset-x-0 before:top-1/2 before:origin-center before:rotate-[-17deg] before:transform before:border-t-4 before:border-text before:content-['']" />
									</span>
								</strong>
							</p>
						)}
						<p className="xl:gap-3 xl:px-4 xl:py-2 inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[clamp(1.125rem,2vw,1.25rem)] font-extrabold text-primary">
							<BanknoteArrowDown size={28} /> OFERTA!
						</p>
					</motion.div>
					<motion.div
						initial={{ opacity: 0, transform: 'scale(1.8) translateY(-20%)' }}
						animate={{ opacity: 1, transform: ['scale(1) translateY(0)'] }}
						transition={{ duration: 0.3, delay: 0.1 }}
						className="xl:mt-11 xl:pl-20 mt-5 flex items-end gap-5 pl-4"
					>
						<div className="flex flex-col items-start text-text">
							<span className="text-[clamp(1.25rem,3vw,1.875rem)]">Por</span>
							<strong className="text-[clamp(1.875rem,5vw,3rem)] font-extrabold">
								R$
							</strong>
						</div>
						<motion.div
							animate={{ scale: [1, 1.4, 1] }}
							transition={{
								duration: 0.3,
								delay: 1.5,
								repeatDelay: 2,
								repeat: 1,
							}}
							className="flex items-start text-secondary"
						>
							<span className="text-[clamp(4rem,12vw,10rem)] font-extrabold leading-[100%]">
								{props.price.toLocaleString('pt-BR').split(',')[0]}
							</span>
							<span className="text-[clamp(2rem,5vw,4rem)] font-extrabold leading-[100%]">
								,
								{
									props.price
										.toLocaleString('pt-BR', {
											currency: 'BRL',
											minimumFractionDigits: 2,
										})
										.split(',')[1]
								}
							</span>
						</motion.div>
					</motion.div>
					{props.fullPrice && (
						<motion.p
							animate={{ scale: [0, 1, 1, 1.4, 1] }}
							transition={{
								duration: 3,
								times: [0, 0.1, 0.58, 0.68, 0.75],
								delay: 0.4,
							}}
							className="w-content xl:-bottom-6 xl:-right-32 absolute -bottom-4 -right-20 mt-5 flex items-center gap-3 rounded-md bg-secondary px-3 py-2 text-[clamp(1.5rem,2.5vw,1.875rem)] font-bold text-primary"
						>
							-
							{Math.round(
								((props.fullPrice - props.price) / props.fullPrice) * 100,
							)}
							% off
						</motion.p>
					)}
					{!!(props.promotionalQuantity && props.promotionalPrice) && (
						<motion.p
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 1, delay: 0.5 }}
							className="absolute -bottom-12 -right-32 text-[clamp(1rem,1.5vw,1.25rem)] font-bold text-text"
						>
							*ACIMA DE {props.promotionalQuantity} {props.unit} SAI POR R${' '}
							{props.promotionalPrice?.toLocaleString('pt-BR', {
								currency: 'BRL',
								minimumFractionDigits: 2,
							})}
						</motion.p>
					)}
					<motion.div
						initial={{ opacity: 0, transform: 'translateY(20%)' }}
						animate={{ opacity: 1, transform: 'translateY(0)' }}
						transition={{ duration: 1, delay: 0.2 }}
						className="xl:-bottom-36 xl:mt-24 xl:pl-20 absolute -bottom-20 mt-16 pl-4 text-[clamp(1.125rem,2vw,1.5rem)] font-medium text-text"
					>
						<p className="flex items-center gap-2">
							<Hash size={28} className="text-secondary" />
							<span>{props.id}</span>
						</p>
						<p className="mt-1 flex items-center gap-2">
							<ScanBarcode size={28} className="text-secondary" />
							<span>{props.barcode}</span>
						</p>
					</motion.div>
				</div>
			</motion.div>
			<motion.div
				className="absolute -right-2 -top-7"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 1, delay: 0.5 }}
			>
				<img
					src={settings.logo}
					alt=""
					className="h-[clamp(2.5rem,8vw,100rem)]"
				/>
			</motion.div>
			<motion.p
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 1, delay: 0.5 }}
				className="xl:text-base absolute bottom-1 left-1/3 text-xs font-bold text-text"
			>
				OFERTA VÁLIDA ENQUANTO DURAR O ESTOQUE OU ATUALIZAÇÃO DE CADASTRO.
			</motion.p>
			<div className="xl:-right-7 xl:bottom-12 xl:w-[35rem] absolute -right-6 bottom-8 flex w-[30rem] -rotate-[20deg] bg-secondary py-1 text-[clamp(1.25rem,1.5vw,1.5rem)] font-extrabold text-primary">
				<ScrollingText speed={10}>
					<div className="flex items-center gap-5">
						<p className="font-bold">Super OFERTA</p>
						<BadgePercent size={28} />
						<p className="font-bold">Super OFERTA</p>
						<BadgePercent size={28} />
						<p className="font-bold">Super OFERTA</p>
					</div>
				</ScrollingText>
			</div>
		</div>
	);
}

function CardSale2(props: CardSaleProps) {
	const { settings } = useTheme();

	return (
		<div className="relative grid h-full w-full grid-cols-2">
			<div className="relative z-10">
				<div className="absolute left-1/2 top-1/2 flex h-[70%] max-h-[80%] w-full -translate-x-1/2 -translate-y-1/2 justify-center">
					<motion.div
						initial={{ opacity: 0, x: -100 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{ duration: 1.5 }}
						className="h-full w-[auto]"
					>
						<img
							src={props.imageUrl}
							alt={props.name}
							className="h-full w-auto rounded-[20px] object-cover"
						/>
					</motion.div>
				</div>
			</div>
			<div className="relative text-secondary">
				<div className="absolute -right-[5%] -top-[15%]">
					<motion.div
						animate={{ rotate: 360, scale: [1.5] }}
						transition={{ duration: 100, repeat: Infinity }}
					>
						<Star />
					</motion.div>
				</div>
				<motion.div className="xl:mt-52 xl:pl-24 relative z-10 mt-28 flex flex-col items-start justify-center pl-0 pr-6">
					<motion.h1
						initial={{ opacity: 0, transform: 'translateX(20%)' }}
						animate={{ opacity: 1, transform: 'translateX(0)' }}
						transition={{ duration: 1 }}
						className="xl:mb-7 mb-3 line-clamp-3 text-[clamp(2.25rem,4vw,3rem)] font-extrabold uppercase leading-[100%] text-text"
					>
						{props.name}
					</motion.h1>
					<div className="relative mt-5">
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(-20%)' }}
							animate={{ opacity: 1, transform: 'translateY(0)' }}
							transition={{ duration: 1, delay: 0.5 }}
							className="flex items-center gap-6 pl-5"
						>
							{props.fullPrice && (
								<p className="text-[clamp(1.25rem,2vw,1.875rem)] text-text">
									De{' '}
									<strong className="relative">
										R${' '}
										<span className="relative">
											<span className="font-bold">
												{props.fullPrice.toLocaleString('pt-BR', {
													minimumFractionDigits: 2,
													currency: 'BRL',
												})}
											</span>
											<span className="pointer-events-none absolute inset-0 before:absolute before:inset-x-0 before:top-1/2 before:origin-center before:rotate-[-17deg] before:transform before:border-t-4 before:border-text before:content-['']" />
										</span>
									</strong>
								</p>
							)}
							<p className="xl:gap-3 xl:px-4 xl:py-2 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-1 text-[clamp(1.125rem,2vw,1rem)] font-extrabold text-secondary">
								<BanknoteArrowDown size={28} /> OFERTA!
							</p>
						</motion.div>
						<motion.div
							initial={{
								opacity: 0,
								transform: 'scale(1.8) translateY(-20%)',
							}}
							animate={{ opacity: 1, transform: ['scale(1) translateY(0)'] }}
							transition={{ duration: 0.3, delay: 0.1 }}
							className="xl:mt-11 xl:pl-20 mt-5 flex items-end gap-5 pl-8"
						>
							<div className="flex flex-col items-start text-text">
								<span className="text-[clamp(1.25rem,2.5vw,1.875rem)]">
									Por
								</span>
								<strong className="text-[clamp(1.875rem,4vw,3rem)] font-extrabold">
									R$
								</strong>
							</div>
							<motion.div
								animate={{ scale: [1, 1.4, 1] }}
								transition={{
									duration: 0.3,
									delay: 1.5,
									repeatDelay: 2,
									repeat: 1,
								}}
								className="flex items-start text-primary"
							>
								<span className="text-[clamp(4rem,12vw,10rem)] font-extrabold leading-[100%]">
									{props.price.toLocaleString('pt-BR').split(',')[0]}
								</span>
								<span className="text-[clamp(2rem,5vw,4rem)] font-extrabold leading-[100%]">
									,
									{
										props.price
											.toLocaleString('pt-BR', {
												currency: 'BRL',
												minimumFractionDigits: 2,
											})
											.split(',')[1]
									}
								</span>
							</motion.div>
						</motion.div>
						{props.fullPrice && (
							<motion.p
								animate={{ scale: [0, 1, 1, 1.4, 1] }}
								transition={{
									duration: 3,
									times: [0, 0.1, 0.58, 0.68, 0.75],
									delay: 0.4,
								}}
								className="w-content xl:-bottom-6 xl:-right-32 xl:mt-5 absolute -right-16 bottom-4 mt-0 flex items-center gap-3 rounded-md bg-primary px-3 py-2 text-[clamp(1.5rem,2vw,1.875rem)] font-bold text-secondary"
							>
								-
								{Math.round(
									((props.fullPrice - props.price) / props.fullPrice) * 100,
								)}
								% off
							</motion.p>
						)}
						{props.promotionalQuantity && props.promotionalPrice ? (
							<motion.p
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ duration: 1, delay: 0.5 }}
								className="absolute -bottom-12 -right-32 text-[clamp(1rem,1vw,1.25rem)] font-bold text-text"
							>
								*ACIMA DE {props.promotionalQuantity} {props.unit} SAI POR R${' '}
								{props.promotionalPrice?.toLocaleString('pt-BR', {
									currency: 'BRL',
									minimumFractionDigits: 2,
								})}
							</motion.p>
						) : null}
						<motion.div
							initial={{ opacity: 0, transform: 'translateY(20%)' }}
							animate={{ opacity: 1, transform: 'translateY(0)' }}
							transition={{ duration: 1, delay: 0.2 }}
							className="xl:-bottom-36 xl:mt-24 absolute -bottom-20 mt-12 pl-20 text-[clamp(1.125rem,2vw,1.5rem)] font-medium text-text"
						>
							<p className="flex items-center gap-2">
								<Hash size={28} className="text-primary" />
								<span>{props.id}</span>
							</p>
							<p className="mt-1 flex items-center gap-2">
								<ScanBarcode size={28} className="text-primary" />
								<span>{props.barcode}</span>
							</p>
						</motion.div>
					</div>
				</motion.div>
			</div>
			<motion.div
				className="absolute -bottom-12 left-1/3"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 1, delay: 0.5 }}
			>
				<img
					src={settings.logo}
					alt=""
					className="h-[clamp(2.5rem,8vw,50rem)]"
				/>
			</motion.div>
			<motion.p
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 1, delay: 0.5 }}
				className="xl:text-base absolute left-1 top-1 text-xs font-bold text-text"
			>
				OFERTA VÁLIDA ENQUANTO DURAR O ESTOQUE OU ATUALIZAÇÃO DE CADASTRO.
			</motion.p>
			<div className="xl:-left-7 xl:bottom-12 xl:w-[35rem] absolute -left-6 bottom-0 z-20 flex w-[35rem] rotate-[20deg] bg-secondary py-1 text-[clamp(1.25rem,2vw,1.875rem)] font-extrabold text-primary">
				<ScrollingText speed={10}>
					<div className="flex items-center gap-5">
						<p className="font-bold">Super OFERTA</p>
						<BadgePercent size={28} />
						<p className="font-bold">Super OFERTA</p>
						<BadgePercent size={28} />
						<p className="font-bold">Super OFERTA</p>
					</div>
				</ScrollingText>
			</div>
		</div>
	);
}

export const CardsSale = { CardSale1, CardSale2 };
